/**
 * Tek bir pikselin GI + AO değeri.
 *
 * Algoritma SSGINode.js ile aynı: GTAO ufuk dilimleri (slice) + 32 bitlik görünürlük
 * maskesi (SSILVB). Tek fark: uv ve piksel dışarıdan gelir. Seyrek geçiş küçük bir
 * hedefe çizdiği için orada uv() / screenCoordinate ekrandaki pikseli göstermez.
 *
 * Çıktı: vec4( gi.rgb, ao ). ao = 1 - kapanan oran (üs yok; üs birleştirmede uygulanır).
 *
 * Kaynak: Activision GTAO, cdrinmatane SSRT3 / SSILVB.
 */

import {
	Fn, Loop, If, Break, float, uint, vec2, vec3, vec4, bool,
	abs, sqrt, pow, mul, div, sub, max, clamp, dot, cross, normalize, sign,
	cos, sin, acos, ceil, fract, shiftRight, countOneBits, luminance,
	PI, HALF_PI, rand, interleavedGradientNoise, getViewPosition,
} from 'three/tsl';

const MAX_RAY = 32; // yarım küre 32 açı dilimine bölünür (bit maskesi)

/**
 * @param {Object} node - SparseGiV2Node (doku ve uniform'lar buradan okunur).
 * @return {Function} traceGiAo( uv, pixel ) → vec4( gi, ao )
 */
export function buildHorizonTrace( node ) {

	const maxRay = uint( MAX_RAY );
	const occludedBits = uint( 0 ); // bir dilim boyunca kapanan açı dilimleri

	const sampleDepth = ( uv ) => node.depthNode.sample( uv ).r;
	const sampleNormal = ( uv ) => node.normalNode.sample( uv ).rgb.normalize();
	const sampleBeauty = ( uv ) => node.beautyNode.sample( uv );

	// 2×2 Bayer benzeri başlangıç kayması (piksel koordinatına göre).
	const spatialOffset = Fn( ( [ position ] ) => {

		return float( 0.25 ).mul( sub( position.y, position.x ).bitAnd( 3 ) );

	} ).setLayout( {
		name: 'sparseGiV2SpatialOffset',
		type: 'float',
		inputs: [ { name: 'position', type: 'vec2' } ]
	} );

	// Ucuz acos yaklaşımı (iki değer birden).
	const fastAcos = Fn( ( [ value ] ) => {

		const outVal = abs( value ).mul( float( - 0.156583 ) ).add( HALF_PI );
		outVal.mulAssign( sqrt( abs( value ).oneMinus() ) );

		const x = value.x.greaterThanEqual( 0 ).select( outVal.x, PI.sub( outVal.x ) );
		const y = value.y.greaterThanEqual( 0 ).select( outVal.y, PI.sub( outVal.y ) );

		return vec2( x, y );

	} ).setLayout( {
		name: 'sparseGiV2FastAcos',
		type: 'vec2',
		inputs: [ { name: 'value', type: 'vec2' } ]
	} );

	// Bir dilimin bir yarısı (sağ ya da sol): ekranda yürür, ufku ve gelen ışığı toplar.
	const horizonSampling = Fn( ( [ directionIsRight, viewPosition, sliceDirTexel, initialRayStep, uvNode, viewDir, viewNormal, n ] ) => {

		const STEP_COUNT = node.stepCount.toConst();
		const EXP_FACTOR = node.expFactor.toConst();
		const THICKNESS = node.thickness.toConst();
		const BACKFACE_LIGHTING = node.backfaceLighting.toConst();
		const RADIUS = node.radius.toConst();

		const stepRadius = float( 0 );

		If( node.useScreenSpaceSampling.equal( true ), () => {

			stepRadius.assign( RADIUS.mul( node._fullResolution.x.div( 2 ) ).div( float( 16 ) ) );

		} ).Else( () => {

			// viewZ eksi işaretli; uzaklaştıkça ekrandaki adım küçülür.
			stepRadius.assign( max( RADIUS.mul( node._halfProjScale ).div( viewPosition.z.negate() ), float( STEP_COUNT ) ) );

		} );

		stepRadius.divAssign( float( STEP_COUNT ).add( 1 ) );
		const radiusVS = max( 1, float( STEP_COUNT.sub( 1 ) ) ).mul( stepRadius );
		const uvDirection = directionIsRight.equal( true ).select( vec2( 1, - 1 ), vec2( - 1, 1 ) );
		const samplingDirection = directionIsRight.equal( true ).select( 1, - 1 );

		const color = vec3( 0 );

		Loop( { start: uint( 0 ), end: STEP_COUNT, type: 'uint', condition: '<' }, ( { i } ) => {

			// Yakın adımlar sık, uzak adımlar seyrek (t^expFactor).
			const offset = pow( abs( mul( stepRadius, float( i ).add( initialRayStep ) ).div( radiusVS ) ), EXP_FACTOR ).mul( radiusVS ).toConst();
			const uvOffset = sliceDirTexel.mul( max( offset, float( i ).add( 1 ) ) ).toConst();
			const sampleUV = uvNode.add( uvOffset.mul( uvDirection ) ).toConst();

			If( sampleUV.x.lessThanEqual( 0 ).or( sampleUV.y.lessThanEqual( 0 ) ).or( sampleUV.x.greaterThanEqual( 1 ) ).or( sampleUV.y.greaterThanEqual( 1 ) ), () => {

				Break();

			} );

			const sampleViewPosition = getViewPosition( sampleUV, sampleDepth( sampleUV ), node._cameraProjectionMatrixInverse ).toConst();
			const pixelToSample = sampleViewPosition.sub( viewPosition ).normalize().toConst();
			const linearThicknessMultiplier = node.useLinearThickness.equal( true ).select( sampleViewPosition.z.negate().div( node._cameraFar ).clamp().mul( 100 ), float( 1 ) );
			const pixelToSampleBackface = normalize( sampleViewPosition.sub( linearThicknessMultiplier.mul( viewDir ).mul( THICKNESS ) ).sub( viewPosition ) );

			// Ön ve arka yüzün ufuk açıları → 0..1 aralığı.
			let frontBackHorizon = vec2( dot( pixelToSample, viewDir ), dot( pixelToSampleBackface, viewDir ) );
			frontBackHorizon = fastAcos( clamp( frontBackHorizon, - 1, 1 ) );
			frontBackHorizon = clamp( div( mul( samplingDirection, frontBackHorizon.negate() ).sub( n.sub( HALF_PI ) ), PI ) );
			frontBackHorizon = directionIsRight.equal( true ).select( frontBackHorizon.yx, frontBackHorizon.xy );

			const minHorizon = frontBackHorizon.x.toConst();
			const maxHorizon = frontBackHorizon.y.toConst();

			// Ufuk aralığını 32 bite yaz; yalnız daha önce kapanmamış bitler "yeni görünür".
			const startHorizonInt = uint( frontBackHorizon.mul( float( maxRay ) ) ).toConst();
			const angleHorizonInt = uint( ceil( maxHorizon.sub( minHorizon ).mul( float( maxRay ) ) ) ).toConst();
			const angleHorizonBitfield = angleHorizonInt.greaterThan( uint( 0 ) ).select( uint( shiftRight( uint( 0xFFFFFFFF ), uint( 32 ).sub( maxRay ).add( maxRay.sub( angleHorizonInt ) ) ) ), uint( 0 ) ).toConst();
			let currentOccludedBits = angleHorizonBitfield.shiftLeft( startHorizonInt );
			currentOccludedBits = currentOccludedBits.bitAnd( occludedBits.bitNot() );

			occludedBits.assign( occludedBits.bitOr( currentOccludedBits ) );
			const newlyVisible = countOneBits( currentOccludedBits );

			// Yeni görünen dilim varsa o noktanın ışığı bu piksele gelir.
			If( newlyVisible.greaterThan( 0 ), () => {

				const lightColor = sampleBeauty( sampleUV );

				If( luminance( lightColor ).greaterThan( 0.001 ), () => {

					const lightDirectionVS = normalize( pixelToSample );
					const normalDotLight = clamp( dot( viewNormal, lightDirectionVS ) );

					If( normalDotLight.greaterThan( 0.001 ), () => {

						const lightNormalVS = sampleNormal( sampleUV );
						let lightNormalDotLight = dot( lightNormalVS, lightDirectionVS.negate() );

						const d = sign( lightNormalDotLight ).lessThan( 0 ).select( abs( lightNormalDotLight ).mul( BACKFACE_LIGHTING ), abs( lightNormalDotLight ) );
						lightNormalDotLight = BACKFACE_LIGHTING.greaterThan( 0 ).and( dot( lightNormalVS, viewDir ).greaterThan( 0 ) ).select( d, clamp( lightNormalDotLight ) );

						color.rgb.addAssign( float( newlyVisible ).div( float( maxRay ) ).mul( lightColor ).mul( normalDotLight ).mul( lightNormalDotLight ) );

					} );

				} );

			} );

		} );

		return vec3( color );

	} );

	return Fn( ( [ uvNode, pixel ] ) => {

		const depth = sampleDepth( uvNode ).toVar();
		const viewPosition = getViewPosition( uvNode, depth, node._cameraProjectionMatrixInverse ).toVar();
		const viewNormal = sampleNormal( uvNode ).toVar();
		const viewDir = normalize( viewPosition.negate() ).toVar();

		// Gürültü: piksel yerine göre + kareye göre döner.
		const noiseOffset = spatialOffset( pixel );
		const noiseDirection = interleavedGradientNoise( pixel );
		const noiseJitter = node._temporalDirection.mul( 0.02 );
		const initialRayStep = fract( noiseOffset.add( node._temporalOffset ) ).add( rand( uvNode.add( noiseJitter ).mul( 2 ).sub( 1 ) ) );

		const ao = float( 0 );
		const color = vec3( 0 );
		const SLICE_COUNT = node.sliceCount.toConst();

		Loop( { start: uint( 0 ), end: SLICE_COUNT, type: 'uint', condition: '<' }, ( { i } ) => {

			// Dilim: ekran düzleminde bir yön. Düzlem = sliceDir × viewDir.
			const rotationAngle = mul( float( i ).add( noiseDirection ).add( node._temporalDirection ), PI.div( float( SLICE_COUNT ) ) ).toConst();
			const sliceDir = vec3( vec2( cos( rotationAngle ), sin( rotationAngle ) ), 0 ).toConst();
			const sliceDirTexel = sliceDir.xy.mul( float( 1 ).div( node._fullResolution ) ).toConst();

			const planeNormal = normalize( cross( sliceDir, viewDir ) ).toConst();
			const tangent = cross( viewDir, planeNormal ).toConst();
			const projectedNormal = viewNormal.sub( planeNormal.mul( dot( viewNormal, planeNormal ) ) ).toConst();
			const projectedNormalNormalized = normalize( projectedNormal ).toConst();

			const cosN = clamp( dot( projectedNormalNormalized, viewDir ), - 1, 1 ).toConst();
			const n = sign( dot( projectedNormal, tangent ) ).negate().mul( acos( cosN ) ).toConst();

			occludedBits.assign( 0 );

			color.addAssign( horizonSampling( bool( true ), viewPosition, sliceDirTexel, initialRayStep, uvNode, viewDir, viewNormal, n ) );
			color.addAssign( horizonSampling( bool( false ), viewPosition, sliceDirTexel, initialRayStep, uvNode, viewDir, viewNormal, n ) );

			ao.addAssign( float( countOneBits( occludedBits ) ).div( float( maxRay ) ) );

		} );

		ao.divAssign( float( SLICE_COUNT ) );

		color.divAssign( float( SLICE_COUNT ) );
		color.mulAssign( node.giIntensity );

		// Tek bir çok parlak örnek bütün hücreyi boyamasın (HDR üst sınırı 7).
		const maxLuminance = float( 7 ).toConst();
		const currentLuminance = luminance( color );
		color.mulAssign( currentLuminance.greaterThan( maxLuminance ).select( maxLuminance.div( currentLuminance ), float( 1 ) ) );

		return vec4( color, ao.clamp().oneMinus() );

	} );

}
